/**
 * Header Component
 * Shared header with navigation and authentication
 * Styled to the Paiseh! design refresh (Paiseh A.dc)
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { UserMenu } from '@/components/auth/UserMenu'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

/** Rotated "P" badge + wordmark used in the nav and footer */
export const PaisehLogo = ({ size = 'md' }: { size?: 'sm' | 'md' }) => {
  const badge = size === 'sm' ? 'w-7 h-7 text-base rounded-[9px]' : 'w-[34px] h-[34px] text-xl rounded-[11px]'
  const word = size === 'sm' ? 'text-lg' : 'text-2xl'
  return (
    <span className="flex items-center gap-2.5">
      <span
        className={`${badge} bg-paiseh-orange flex items-center justify-center text-white font-display font-extrabold -rotate-6`}
      >
        P
      </span>
      <span className={`${word} font-display font-extrabold text-ink`}>
        Paiseh<span className="text-paiseh-orange">!</span>
      </span>
    </span>
  )
}

export const Header = () => {
  const { user, signInWithGoogle, signOut, upgrading } = useAuth()
  const { flags } = useFeatureFlags()
  const [menuOpen, setMenuOpen] = useState(false)

  const signedIn = user && !user.isAnonymous
  const closeMenu = () => setMenuOpen(false)

  return (
    <header className="sticky top-0 z-50 bg-cream/[.86] backdrop-blur-[10px] border-b border-ink/[.07]">
      <div className="max-w-[1180px] mx-auto flex items-center justify-between px-5 sm:px-10 py-[18px]">
        <Link to="/" aria-label="Paiseh! home" onClick={closeMenu}>
          <PaisehLogo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-[30px] text-[15px] font-semibold text-paiseh-muted font-jakarta">
          <Link to="/wishlists" className="hover:text-ink transition-colors">
            My Wishlists
          </Link>
          <Link to="/reservations" className="hover:text-ink transition-colors">
            Reservations
          </Link>
          {flags.enableSecretSanta && (
            <Link to="/secret-santa" className="hover:text-ink transition-colors">
              Secret Santa
            </Link>
          )}
          {signedIn ? (
            <UserMenu />
          ) : (
            <>
              <button
                onClick={() => signInWithGoogle().catch(() => {})}
                disabled={upgrading}
                className="text-paiseh-muted hover:text-ink transition-colors disabled:opacity-50"
              >
                {upgrading ? 'Signing in…' : 'Sign in'}
              </button>
              <Link
                to="/wishlists"
                className="bg-ink text-cream px-[22px] py-[11px] rounded-full hover:opacity-90 transition-opacity"
              >
                Create a list
              </Link>
            </>
          )}
        </nav>

        {/* Mobile controls */}
        <div className="flex sm:hidden items-center gap-2.5 font-jakarta font-semibold">
          <Link
            to="/wishlists"
            onClick={closeMenu}
            className="bg-ink text-cream text-sm px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            Create a list
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={menuOpen}
            className="w-10 h-10 flex items-center justify-center rounded-full text-ink hover:bg-ink/[.06] transition-colors"
          >
            {menuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40 sm:hidden" onClick={closeMenu} aria-hidden="true" />
          <nav className="sm:hidden relative z-50 border-t border-ink/[.07] bg-cream max-w-[1180px] mx-auto px-5 py-2 flex flex-col font-jakarta font-semibold text-[15px] text-paiseh-muted">
            <Link to="/wishlists" onClick={closeMenu} className="py-3 hover:text-ink transition-colors">
              My Wishlists
            </Link>
            <Link to="/reservations" onClick={closeMenu} className="py-3 hover:text-ink transition-colors">
              Reservations
            </Link>
            {flags.enableSecretSanta && (
              <Link to="/secret-santa" onClick={closeMenu} className="py-3 hover:text-ink transition-colors">
                Secret Santa
              </Link>
            )}
            <div className="border-t border-ink/[.07] my-1" />
            {signedIn ? (
              <>
                <div className="py-2 text-ink">{user?.displayName || user?.email || 'Account'}</div>
                <button
                  onClick={() => {
                    signOut().catch(() => {})
                    closeMenu()
                  }}
                  className="py-3 text-left text-red-600"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  signInWithGoogle().catch(() => {})
                  closeMenu()
                }}
                disabled={upgrading}
                className="py-3 text-left text-ink disabled:opacity-50"
              >
                {upgrading ? 'Signing in…' : 'Sign in with Google'}
              </button>
            )}
          </nav>
        </>
      )}
    </header>
  )
}
